import React, { useState } from "react";
import {
  Typography,
  Button,
  Grid,
  Card,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CardContent,
  Divider,
  Modal,
} from "@mui/material";
import {
  ExpandMoreOutlined,
  GroupAddOutlined,
  KeyboardBackspaceOutlined,
  PersonAddOutlined,
} from "@mui/icons-material";
import UserProfileList, { ProfileResponseData } from "./UserProfileList";
import FormApprovationRequest from "./FormApprovationRequest";
import SnackbarProvider from "app/components/SnackbarProvider";
import { NavLink } from "react-router-dom";
import { notify } from "app/utils/notifyUtils";

const ProfileInfo = ({
  profile,
  fullData,
  openModal,
  setSelectedProfileItem,
}) => {
  return (
    <Accordion
      sx={{
        marginTop: 1,
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        },
        borderRadius: "8px",
        border: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlined />}
        aria-controls="panel1-content"
        id="panel1-header"
        sx={{
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.02)",
          },
          minHeight: 72,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <img
            src={require(`../../../assets/icons/${profile.icon}.png`)}
            alt={profile.icon}
            width={60}
            style={{ marginRight: 16 }}
          />
          <div>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 500 }}>
              {profile.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Clique para ver detalhes
            </Typography>
          </div>
        </div>
      </AccordionSummary>
      {[4].includes(profile.id) && (
        <AccordionDetails sx={{ padding: "16px 24px" }}>
          <Typography variant="body2">
            Solicite um perfil para ter acesso às funcionalidades do sistema.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Não esqueça de preencher suas{" "}
            <NavLink
              to={"#"}
              style={{
                color: "#1976d2",
                textDecoration: "none",
              }}
              onClick={() => setSelectedProfileItem("profileUserAddress")}
            >
              Informações de Contato
            </NavLink>
            !
          </Typography>
        </AccordionDetails>
      )}

      {[2, 5, 7, 12].includes(profile.id) && (
        <AccordionDetails sx={{ padding: "16px 24px" }}>
          <Typography variant="body2" sx={{ color: "success.main" }}>
            Você foi aprovado por um administrador!
          </Typography>
        </AccordionDetails>
      )}
      {![2, 4, 5, 7, 12].includes(profile.id) && (
        <AccordionDetails sx={{ padding: "16px 24px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Responsável(is):
              </Typography>
              {fullData.profile_approvations
                .filter((item) => item.profile_id === profile.id)
                .map((item) => (
                  <Typography variant="body2" key={item.id}>
                    {item.manager.name}
                  </Typography>
                ))}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => openModal(profile)}
                startIcon={<GroupAddOutlined />}
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                }}
              >
                Adicionar Responsável
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      )}
    </Accordion>
  );
};
const UserActiveProfiles = ({ profiles, setSelectedProfileItem }) => {
  const [requestNewProfile, setRequestNewProfile] = useState<boolean>(false);
  const [approvationModalOpen, setApprovationModalOpen] = React.useState(false);
  const handleOpen = () => setApprovationModalOpen(true);
  const handleClose = () => setApprovationModalOpen(false);
  const [selectedProfile, setSelectedProfile] = useState<ProfileResponseData>();
  const snackbarProvider = SnackbarProvider();

  const handleClick = (value: ProfileResponseData) => {
    setSelectedProfile(value);
    handleOpen();
  };

  const newManagerSuccessCallback = () => {
    notify(
      "success",
      "Novo responsável solicitado com sucesso aguarda a aprovação"
    );
    handleClose();
  };

  if (!requestNewProfile) {
    return (
      <div>
        <Card>
          <CardContent>
            <Grid container>
              <Grid
                item
                xs={12}
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    marginBottom: 1,
                    fontWeight: 600,
                    color: "#1a237e",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Meus Perfis
                </Typography>{" "}
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setRequestNewProfile(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 2,
                    py: 1,
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                    "&:hover": {
                      boxShadow: "0 6px 16px rgba(25, 118, 210, 0.3)",
                    },
                  }}
                >
                  <PersonAddOutlined
                    fontSize="small"
                    style={{ marginRight: 5 }}
                  />{" "}
                  Solicitar Perfil
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ marginBottom: 3, color: "#000" }}
                >
                  Lista de perfis ativos para o seu usuário
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {profiles.profiles.map((profile: any) => (
                  <ProfileInfo
                    key={profile.id}
                    profile={profile}
                    fullData={profiles}
                    openModal={handleClick}
                    setSelectedProfileItem={setSelectedProfileItem}
                  />
                ))}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Modal
          open={approvationModalOpen}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div>
            <FormApprovationRequest
              selectedProfile={selectedProfile}
              reloadPrevComponent={newManagerSuccessCallback}
              handleClose={handleClose}
            />
          </div>
        </Modal>
      </div>
    );
  }
  if (requestNewProfile) {
    return (
      <Card elevation={3} sx={{ borderRadius: 2, backgroundColor: "#fff" }}>
        <CardContent sx={{ padding: 3 }}>
          <Grid container spacing={1}>
            <Grid
              item
              xs={12}
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  marginBottom: 1,
                  fontWeight: 600,
                  color: "#1a237e",
                  letterSpacing: "-0.5px",
                }}
              >
                Solicitar novo perfil
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => setRequestNewProfile(false)}
                startIcon={<KeyboardBackspaceOutlined />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Voltar
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "#424242",
                  fontSize: "1.1rem",
                  lineHeight: 1.5,
                }}
              >
                Para utilizar o laboratório multiusuário do LCE você deverá
                solicitar um dos perfis abaixo.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <UserProfileList />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <div style={{ height: "10vh", textAlign: "center" }}>
      <Typography variant="h6"> Falha ao Renderizar a página </Typography>
    </div>
  );
};

export default UserActiveProfiles;
